// OrganisationCreationHelp.tsx
import React, { useMemo, useRef, useState } from "react";
import {
  Drawer,
  IconButton,
  TextField,
  Typography,
  Box,
  Divider,
  Chip,
  Stack,
  List,
  ListItem,
  ListItemText,
  Tooltip,
  Grid,
  Card,
  CardContent,
  Stepper,
  Step,
  StepLabel,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Alert,
  Menu,
  MenuItem,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import BusinessIcon from "@mui/icons-material/Business";
import LinkIcon from "@mui/icons-material/Link";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

type PDFLink = { title: string; href: string };
type StepBlock = { title: string; bullets: string[] };

const DEFAULT_PDFS = [
  {
    title: "Organisation Creation Admin Guide",
    href: "https://mdimages-test.s3.amazonaws.com/93ee0643-060c-41b7-b1ac-286930e429f8_Organisation_Creation_Admin_Guide.pdf",
  },
];

// ---- Static Content ----

const STAGES: StepBlock[] = [
  { title: "Stage 1 — Create the Organisation", bullets: ["Register the client's organisation in the Machadalo Super Admin account."] },
  { title: "Stage 2 — Organisation Mapping", bullets: ["Link the new organisation bidirectionally to Machadalo."] },
  { title: "Stage 3 — Admin Setup", bullets: ["Create an admin profile and an admin user under the new organisation."] },
];

const ORG_FORM_FIELDS = [
  { field: "Organisation Name", required: "Yes", notes: "The full legal or trading name of the client organisation." },
  { field: "Mobile Number", required: "Yes", notes: "Primary contact mobile number for the organisation." },
  { field: "Email", required: "Yes", notes: "Primary contact email address for the organisation." },
  { field: "Category", required: "Yes", notes: "Always select 'Business'. Do not change this." },
  { field: "Business Type", required: "Yes", notes: "The industry or sector the client operates in." },
  { field: "Business Sub Type", required: "Yes", notes: "A more specific classification under the Business Type." },
  { field: "Default Session Timeout", required: "Yes", notes: "The inactivity timeout duration for user sessions." },
  { field: "Upload Logo", required: "Optional", notes: "Client branding logo. Can be added later." },
];

const MAPPINGS = [
  { direction: "Business → Machadalo", source: "The new organisation you just created", destination: "Machadalo" },
  { direction: "Machadalo → Business", source: "Machadalo", destination: "The new organisation you just created" },
];

const SectionHeader: React.FC<{ title: string; subtitle?: string; id?: string }> = ({ title, subtitle, id }) => (
  <Box id={id} sx={{ mb: 1.25, mt: 3 }}>
    <Typography variant="subtitle1" fontWeight={700}>
      {title}
    </Typography>
    {subtitle && (
      <Typography variant="caption" color="text.secondary">
        {subtitle}
      </Typography>
    )}
  </Box>
);

const StepAccordion: React.FC<{ steps: StepBlock[]; label: string; defaultExpanded?: boolean }> = ({
  steps,
  label,
  defaultExpanded,
}) => {
  const [active, setActive] = useState(0);
  return (
    <Accordion defaultExpanded={defaultExpanded} disableGutters sx={{ mb: 2, borderRadius: 2 }}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Typography fontWeight={600}>
          {label} ({steps.length})
        </Typography>
      </AccordionSummary>
      <AccordionDetails>
        <Stepper activeStep={active} alternativeLabel sx={{ mb: 2, flexWrap: "wrap" }}>
          {steps.map((s, i) => (
            <Step key={s.title} onClick={() => setActive(i)}>
              <StepLabel>{s.title}</StepLabel>
            </Step>
          ))}
        </Stepper>
        {steps.map((s, idx) => {
          const isActive = idx === active;
          return (
            <Box
              key={s.title}
              sx={{
                mb: 1.25,
                p: 1.25,
                border: (t) => `1px dashed ${t.palette.divider}`,
                borderRadius: 2,
                bgcolor: (t) => (isActive ? t.palette.action.hover : "transparent"),
              }}
            >
              <Typography fontWeight={600} sx={{ mb: 0.5 }}>
                {idx + 1}. {s.title}
              </Typography>
              <List dense sx={{ pt: 0 }}>
                {s.bullets.map((b, j) => (
                  <ListItem key={j} sx={{ py: 0.2 }}>
                    <ListItemText primaryTypographyProps={{ variant: "body2" }} primary={`• ${b}`} />
                  </ListItem>
                ))}
              </List>
            </Box>
          );
        })}
        <Stack direction="row" spacing={1}>
          <Button variant="outlined" onClick={() => setActive((p) => Math.max(0, p - 1))}>
            Previous
          </Button>
          <Button variant="contained" onClick={() => setActive((p) => Math.min(steps.length - 1, p + 1))}>
            Next
          </Button>
        </Stack>
      </AccordionDetails>
    </Accordion>
  );
};

const OrganisationCreationHelp: React.FC<{ pdfs?: PDFLink[] }> = ({ pdfs }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [menuEl, setMenuEl] = useState<null | HTMLElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  const jumpTo = (hash: string) => {
    const el = contentRef.current?.querySelector(hash);
    if (el) (el as HTMLElement).scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const handleOpenMenu = (e: React.MouseEvent<HTMLElement>) => setMenuEl(e.currentTarget);
  const handleCloseMenu = () => setMenuEl(null);
  const handlePrint = () => {
    window.open(DEFAULT_PDFS[0].href, "_blank");
  };

  const visiblePdfs = pdfs && pdfs.length > 0 ? pdfs : DEFAULT_PDFS;

  const sectionTexts = useMemo(() => {
    const flatten = (steps: StepBlock[]) => steps.map((s) => `${s.title} ${s.bullets.join(" ")}`).join(" ");

    return {
      overview: "organisation onboarding client Machadalo Super Admin account",
      stages: flatten(STAGES) + " create organisation mapping admin setup",
      createOrg: "management organisations add new organisation form business category",
      mapping: "business to machadalo machadalo to business bidirectional mapping",
      adminSetup: "admin profile admin user permissions login access",
    };
  }, []);

  const q = query.trim().toLowerCase();
  const matches = (sectionKey: keyof typeof sectionTexts) =>
    !q || sectionTexts[sectionKey].toLowerCase().includes(q);

  return (
    <div>
      <Tooltip title="Organisation Creation Help">
        <IconButton onClick={() => setIsOpen(true)}>
          <HelpOutlineIcon fontSize="medium" />
        </IconButton>
      </Tooltip>

      <Drawer anchor="right" open={isOpen} onClose={() => setIsOpen(false)} sx={{ zIndex: 1400 }}>
        <Box
          sx={{
            width: 560,
            display: "flex",
            flexDirection: "column",
            height: "100%",
            bgcolor: (t) => (t.palette.mode === "light" ? "#fff" : "background.paper"),
          }}
        >
          {/* Header */}
          <Box
            sx={{
              px: 2,
              py: 1.25,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              borderBottom: (t) => `1px solid ${t.palette.divider}`,
              position: "sticky",
              top: 0,
              zIndex: 2,
              background: "linear-gradient(90deg, rgba(99,102,241,0.16), rgba(14,165,233,0.16))",
              backdropFilter: "saturate(1.2)",
            }}
          >
            <Stack direction="row" spacing={1} alignItems="center">
              <BusinessIcon />
              <Typography variant="h6" fontWeight={800}>
                Organisation Creation • Help
              </Typography>
            </Stack>

            <Stack direction="row" spacing={1}>
              <Menu
                anchorEl={menuEl}
                open={Boolean(menuEl)}
                onClose={handleCloseMenu}
              >
                {visiblePdfs.map((p) => (
                  <MenuItem
                    key={p.title}
                    component={p.href ? "a" : "li"}
                    href={p.href || undefined}
                    target={p.href ? "_blank" : undefined}
                    rel={p.href ? "noopener noreferrer" : undefined}
                    onClick={handleCloseMenu}
                  >
                    <PictureAsPdfIcon fontSize="small" style={{ marginRight: 8 }} />
                    {p.title}
                  </MenuItem>
                ))}
              </Menu>
              <Tooltip title="Print / Save as PDF">
                <IconButton onClick={handlePrint}>
                  <PictureAsPdfIcon />
                </IconButton>
              </Tooltip>
              <IconButton onClick={() => setIsOpen(false)}>
                <CloseIcon />
              </IconButton>
            </Stack>
          </Box>

          {/* Search + Quick Jump */}
          <Box sx={{ px: 2, pt: 2 }}>
            <TextField
              size="small"
              fullWidth
              placeholder="Search stages, mapping, admin setup, fields…"
              InputProps={{ startAdornment: <SearchIcon sx={{ mr: 1 }} /> }}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
            <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: "wrap" }}>
              <Chip label="Overview" size="small" variant="outlined" onClick={() => jumpTo("#sec-overview")} sx={{ mb: 1 }} />
              <Chip label="Stages" size="small" variant="outlined" onClick={() => jumpTo("#sec-stages")} sx={{ mb: 1 }} />
              <Chip label="Create Org" size="small" variant="outlined" onClick={() => jumpTo("#sec-create-org")} sx={{ mb: 1 }} />
              <Chip label="Mapping" size="small" variant="outlined" onClick={() => jumpTo("#sec-mapping")} sx={{ mb: 1 }} />
              <Chip label="Admin Setup" size="small" variant="outlined" onClick={() => jumpTo("#sec-admin-setup")} sx={{ mb: 1 }} />
            </Stack>
          </Box>

          <Divider sx={{ my: 2 }} />

          {/* Scrollable Content */}
          <Box ref={contentRef} sx={{ px: 2, pb: 3, overflowY: "auto" }}>
            {/* Overview */}
            {matches("overview") && (
              <>
                <SectionHeader id="sec-overview" title="Overview" subtitle="End-to-end process of onboarding a new client organisation." />
                <Card variant="outlined" sx={{ borderRadius: 2, p: 1.5, mb: 1 }}>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    This guide covers the end-to-end process of onboarding a new client organisation onto the Machadalo platform.
                  </Typography>
                  <Alert severity="warning" sx={{ mb: 1 }}>
                    <strong>Super Admin Access Required:</strong> All steps in Stages 1 and 2 must be performed from the Machadalo Super Admin account.
                  </Alert>
                </Card>
              </>
            )}

            {/* Stages */}
            {matches("stages") && (
              <>
                <SectionHeader id="sec-stages" title="Setup Stages" />
                <StepAccordion steps={STAGES} label="Three Stages" defaultExpanded />
              </>
            )}

            {/* Create Organisation */}
            {matches("createOrg") && (
              <>
                <SectionHeader id="sec-create-org" title="Create the Organisation" />
                <Card variant="outlined" sx={{ borderRadius: 2, p: 1.5, mb: 1 }}>
                  <Typography variant="body2" fontWeight={600} sx={{ mb: 1 }}>Steps:</Typography>
                  <List dense sx={{ pt: 0 }}>
                    <ListItem sx={{ py: 0.2 }}>
                      <ListItemText primaryTypographyProps={{ variant: "body2" }} primary="1. Log in to Machadalo Super Admin account" />
                    </ListItem>
                    <ListItem sx={{ py: 0.2 }}>
                      <ListItemText primaryTypographyProps={{ variant: "body2" }} primary="2. Click Management → Organisation → View" />
                    </ListItem>
                    <ListItem sx={{ py: 0.2 }}>
                      <ListItemText primaryTypographyProps={{ variant: "body2" }} primary="3. Click Add to open the New Organisation form" />
                    </ListItem>
                    <ListItem sx={{ py: 0.2 }}>
                      <ListItemText primaryTypographyProps={{ variant: "body2" }} primary="4. Fill in all required fields (see table below)" />
                    </ListItem>
                    <ListItem sx={{ py: 0.2 }}>
                      <ListItemText primaryTypographyProps={{ variant: "body2" }} primary="5. Click Create" />
                    </ListItem>
                  </List>

                  <Typography variant="body2" fontWeight={600} sx={{ mt: 1 }}>Organisation Form — Field Reference</Typography>
                  <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2, mt: 1 }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow sx={{ bgcolor: "action.hover" }}>
                          <TableCell sx={{ fontWeight: 700 }}>Field</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>Required</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>Notes</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {ORG_FORM_FIELDS.map((row) => (
                          <TableRow key={row.field}>
                            <TableCell>{row.field}</TableCell>
                            <TableCell>{row.required}</TableCell>
                            <TableCell>{row.notes}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  <Alert severity="warning" sx={{ mt: 1 }}>
                    <strong>Category:</strong> Always select 'Business'. Do not select any other category.
                  </Alert>
                </Card>
              </>
            )}

            {/* Mapping */}
            {matches("mapping") && (
              <>
                <SectionHeader id="sec-mapping" title="Organisation Mapping" />
                <Card variant="outlined" sx={{ borderRadius: 2, p: 1.5, mb: 1 }}>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    Create two mappings to link the new organisation with Machadalo. Both are required — setting up only one direction will cause integration errors.
                  </Typography>
                  <Grid container spacing={1}>
                    {MAPPINGS.map((m) => (
                      <Grid item xs={12} key={m.direction}>
                        <Card variant="outlined" sx={{ p: 1.5, borderRadius: 2, bgcolor: "action.hover" }}>
                          <Typography fontWeight={600}>{m.direction}</Typography>
                          <Typography variant="body2">Source: {m.source}</Typography>
                          <Typography variant="body2">Destination: {m.destination}</Typography>
                        </Card>
                      </Grid>
                    ))}
                  </Grid>
                </Card>
              </>
            )}

            {/* Admin Setup */}
            {matches("adminSetup") && (
              <>
                <SectionHeader id="sec-admin-setup" title="Admin Setup" />
                <Card variant="outlined" sx={{ borderRadius: 2, p: 1.5, mb: 1 }}>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    With the organisation created and mapped, set up admin access so the client can log in and manage their platform.
                  </Typography>
                  <List dense sx={{ pt: 0 }}>
                    <ListItem sx={{ py: 0.5 }}>
                      <ListItemText primaryTypographyProps={{ variant: "body2" }} primary="1. Create an Admin Profile — defines the permission set for the organisation's administrator." />
                    </ListItem>
                    <ListItem sx={{ py: 0.5 }}>
                      <ListItemText primaryTypographyProps={{ variant: "body2" }} primary="2. Create an Admin User — assign the admin profile to the client's primary administrator." />
                    </ListItem>
                  </List>
                  <Alert severity="info" sx={{ mt: 1 }}>
                    Only the very first Admin Profile and Admin User need to be set up from Machadalo Super Admin — everything afterward is self-service for the organisation's admin.
                  </Alert>
                </Card>
              </>
            )}

            {/* Footer */}
            <Box sx={{ mt: 2, px: 1.5, py: 1, borderTop: (t) => `1px solid ${t.palette.divider}`, borderRadius: 2 }}>
              <Typography variant="caption" color="text.secondary">
                Tip: Use the Download icon in the header to fetch the full manual PDF, or print this page to PDF.
              </Typography>
            </Box>
          </Box>
        </Box>
      </Drawer>
    </div>
  );
};

export default OrganisationCreationHelp;